import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import { canUser, hasMembership, hasRole } from '@/auth/permissions';
import { useAssetRenewStore } from './assetRenewStore';


import {
  fetchEnterpriseLogin,
  fetchGetAccountMe,
  fetchLogoutAllSessions,
  fetchLogoutSession,
  fetchPersonalLogin,
  fetchRefreshSession,
  fetchUpdateAccountAvatar,
  fetchUpdateAccountNickname,
  fetchWeChatMiniProgramPhoneLogin,
  fetchWeChatMiniProgramSMSLogin,
} from '@/service/api/account';
import type {
  AccountType,
  AuthStatus,
  MembershipTier,
  Permission,
  PlatformRole,
  RegisterInput,
} from '@/auth/types';

export const AUTH_STORAGE_KEY = 'opc-auth-session';

function saveAuthTokens(accessToken?: string, refreshToken?: string) {
  if (accessToken) {
    localStorage.setItem('access_token', accessToken);
    localStorage.setItem('token', accessToken);
  }
  if (refreshToken) {
    localStorage.setItem('refresh_token', refreshToken);
    localStorage.setItem('refreshToken', refreshToken);
  }
}

function clearAuthTokens() {
  localStorage.removeItem('access_token');
  localStorage.removeItem('token');
  localStorage.removeItem('refresh_token');
  localStorage.removeItem('refreshToken');
}

export type LoginInput = {
  phone: string;
  code: string;
  accountType?: AccountType | string;
};

interface AuthStore {
  status: AuthStatus;
  user: Api.Account.AccountResponse | null;
  error: string | null;

  // 通用/快捷登录与注册
  login: (input: LoginInput) => Promise<void>;
  register: (input: RegisterInput) => Promise<void>;

  // account.ts 中的对应登录 API 方法
  loginPersonal: (data: Api.Account.AccountLoginRequest) => Promise<Api.Account.AccountLoginResponse>;
  loginEnterprise: (data: Api.Account.AccountLoginRequest) => Promise<Api.Account.AccountLoginResponse>;
  loginWeChatPhone: (data: Api.Account.WeChatMiniProgramPhoneLoginRequest) => Promise<Api.Account.AccountLoginResponse>;
  loginWeChatSMS: (data: Api.Account.WeChatMiniProgramSMSLoginRequest) => Promise<Api.Account.AccountLoginResponse>;
  loginWithSessionResponse: (res: Api.Account.AccountLoginResponse) => Promise<void>;

  loginAs: (user: Api.Account.AccountResponse) => void;
  fetchCurrentAccount: () => Promise<void>;
  updateProfile: (profile: Partial<Api.Account.AccountResponse>) => Promise<void>;
  logout: () => void;
  logoutAll: () => Promise<void>;
  refreshSession: () => Promise<void>;
  clearError: () => void;
  can: (permission: Permission) => boolean;
  hasRole: (role: PlatformRole) => boolean;
  hasMembership: (tier: MembershipTier) => boolean;
  hasAuthorizedContactView: () => boolean;
  isEnterprise: () => boolean;
}

let fetchAccountPromise: Promise<void> | null = null;

export const useAuthStore = create<AuthStore>()(
  persist(
    (set, get) => ({
      status: 'anonymous',
      user: null,
      error: null,

      login: async (input) => {
        set({ status: 'authenticating', error: null });
        try {
          const isEnterprise = input.accountType === 'ENTERPRISE' || input.accountType === 'enterprise';
          const reqData: Api.Account.AccountLoginRequest = {
            phone: input.phone,
            verification_code: input.code,
            agreement_accepted: true,
          };

          const res = isEnterprise
            ? await fetchEnterpriseLogin(reqData)
            : await fetchPersonalLogin(reqData);

          saveAuthTokens(res?.access_token, res?.refresh_token);

          set({ status: 'authenticated', user: res?.account || null, error: null });
        } catch (error) {
          const message = error instanceof Error ? error.message : '登录失败，请稍后重试';
          set({ status: 'anonymous', user: null, error: message });
          throw error;
        }
      },

      loginPersonal: async (data) => {
        set({ status: 'authenticating', error: null });
        try {
          const res = await fetchPersonalLogin(data);
          saveAuthTokens(res?.access_token, res?.refresh_token);
          set({ status: 'authenticated', user: res?.account || null, error: null });
          return res;
        } catch (error) {
          const message = error instanceof Error ? error.message : '个人账号登录失败';
          set({ status: 'anonymous', user: null, error: message });
          throw error;
        }
      },

      loginEnterprise: async (data) => {
        set({ status: 'authenticating', error: null });
        try {
          const res = await fetchEnterpriseLogin(data);
          saveAuthTokens(res?.access_token, res?.refresh_token);
          set({ status: 'authenticated', user: res?.account || null, error: null });
          return res;
        } catch (error) {
          const message = error instanceof Error ? error.message : '企业账号登录失败';
          set({ status: 'anonymous', user: null, error: message });
          throw error;
        }
      },

      loginWeChatPhone: async (data) => {
        set({ status: 'authenticating', error: null });
        try {
          const res = await fetchWeChatMiniProgramPhoneLogin(data);
          saveAuthTokens(res?.access_token, res?.refresh_token);
          set({ status: 'authenticated', user: res?.account || null, error: null });
          return res;
        } catch (error) {
          const message = error instanceof Error ? error.message : '微信手机号快捷登录失败';
          set({ status: 'anonymous', user: null, error: message });
          throw error;
        }
      },

      loginWeChatSMS: async (data) => {
        set({ status: 'authenticating', error: null });
        try {
          const res = await fetchWeChatMiniProgramSMSLogin(data);
          saveAuthTokens(res?.access_token, res?.refresh_token);
          set({ status: 'authenticated', user: res?.account || null, error: null });
          return res;
        } catch (error) {
          const message = error instanceof Error ? error.message : '微信验证码登录失败';
          set({ status: 'anonymous', user: null, error: message });
          throw error;
        }
      },

      loginWithSessionResponse: async (res) => {
        saveAuthTokens(res?.access_token, res?.refresh_token);
        if (res?.account) {
          set({ status: 'authenticated', user: res.account, error: null });
        } else {
          await get().fetchCurrentAccount();
        }
      },

      register: async (input) => {
        set({ status: 'authenticating', error: null });
        try {
          const isEnterprise = input.accountType === 'ENTERPRISE' || input.accountType === 'enterprise';
          const reqData: Api.Account.AccountLoginRequest = {
            phone: input.phone,
            verification_code: input.code,
            agreement_accepted: true,
          };

          const res = isEnterprise
            ? await fetchEnterpriseLogin(reqData)
            : await fetchPersonalLogin(reqData);

          saveAuthTokens(res?.access_token, res?.refresh_token);

          set({ status: 'authenticated', user: res?.account || null, error: null });
        } catch (error) {
          const message = error instanceof Error ? error.message : '注册失败，请稍后重试';
          set({ status: 'anonymous', user: null, error: message });
          throw error;
        }
      },

      loginAs: (user) => {
        set({ status: 'authenticated', user, error: null });
      },

      fetchCurrentAccount: async () => {
        const token = localStorage.getItem('access_token') || localStorage.getItem('token');
        if (!token) return;

        if (fetchAccountPromise) {
          return fetchAccountPromise;
        }

        fetchAccountPromise = (async () => {
          try {
            const account = await fetchGetAccountMe();
            if (account) {
              set({ status: 'authenticated', user: account, error: null });
            }
          } catch (error) {
            console.error('获取当前账号信息失败', error);
          } finally {
            fetchAccountPromise = null;
          }
        })();

        return fetchAccountPromise;
      },

      updateProfile: async (profile) => {
        const currentUser = get().user;
        if (!currentUser) return;

        if (profile.avatar_asset_id) {
          try {
            await fetchUpdateAccountAvatar({ avatar_asset_id: profile.avatar_asset_id });
          } catch (e) {
            console.error('更新头像失败', e);
          }
        }

        if (profile.nick_name) {
          if (currentUser.nickname_editable === false) {
            const err = new Error('暂不支持修改昵称');
            console.error('更新昵称失败', err);
            throw err;
          }
          try {
            await fetchUpdateAccountNickname({ nick_name: profile.nick_name });
          } catch (e) {
            console.error('更新昵称失败', e);
            throw e;
          }
        }

        set({
          user: {
            ...currentUser,
            ...profile,
          },
        });
      },

      logout: () => {
        const token = localStorage.getItem('access_token') || localStorage.getItem('token');
        clearAuthTokens();
        if (token) {
          fetchLogoutSession().catch(() => { });
        }
        useAssetRenewStore.getState().clearAllCache();
        set({ status: 'anonymous', user: null, error: null });
      },

      logoutAll: async () => {
        try {
          await fetchLogoutAllSessions();
        } catch {
          // 忽略退出异常，同样清理本地状态
        }
        clearAuthTokens();
        useAssetRenewStore.getState().clearAllCache();
        set({ status: 'anonymous', user: null, error: null });
      },


      refreshSession: async () => {
        try {
          const res = await fetchRefreshSession();
          saveAuthTokens(res?.access_token, res?.refresh_token);
        } catch (error) {
          console.error('刷新会话失败', error);
        }
      },

      clearError: () => set({ error: null }),
      can: (permission) => canUser(get().user, permission),
      hasRole: (role) => hasRole(get().user, role),
      hasMembership: (tier) => hasMembership(get().user, tier),
      hasAuthorizedContactView: () => {
        const user = get().user;
        const benefits = user?.membership?.subscriptions?.[0]?.benefits_snapshot;
        return Boolean(benefits && (benefits.AUTHORIZED_CONTACT_VIEW === true || benefits.AUTHORIZED_CONTACT_VIEW === 'true'));
      },
      isEnterprise: () => {
        const user = get().user;
        return (user?.account_type || '').toUpperCase() === 'ENTERPRISE';
      },
    }),
    {
      name: AUTH_STORAGE_KEY,
      version: 1,
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({ user: state.user }) as AuthStore,
      onRehydrateStorage: () => (state) => {
        const token = localStorage.getItem('access_token') || localStorage.getItem('token');
        if (token && state) {
          state.fetchCurrentAccount();
        }
      },
      merge: (persistedState, currentState) => {
        const persisted = persistedState as Partial<AuthStore>;
        const user = (persisted?.user && typeof persisted.user === 'object') ? persisted.user : null;
        return {
          ...currentState,
          user,
          status: user ? 'authenticated' : 'anonymous',
        };
      },
    },
  ),
);


